import React from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { dateRangePresets, DateRangePreset } from "../utils/datePresets";
import moment from "moment-timezone";

interface DateRangeSelectorProps {
  value: { from?: Date; to?: Date };
  onChange: (range: { from?: Date; to?: Date }) => void;
  className?: string;
}

export function DateRangeSelector({ value, onChange, className }: DateRangeSelectorProps) {
  const handlePresetClick = (preset: DateRangePreset) => {
    const range = preset.getDateRange();
    onChange(range);
  };

  const handleFromDateChange = (date: Date | undefined) => {
    onChange({
      from: date,
      to: value.to,
    });
  };

  const handleToDateChange = (date: Date | undefined) => {
    onChange({
      from: value.from,
      to: date,
    });
  };

  // Helper function to check if current selection matches a preset
  const isPresetActive = (preset: DateRangePreset): boolean => {
    if (!value.from || !value.to) return false;
    
    const presetRange = preset.getDateRange();
    const currentFromStr = moment(value.from).format("YYYY-MM-DD");
    const currentToStr = moment(value.to).format("YYYY-MM-DD");
    const presetFromStr = moment(presetRange.from).format("YYYY-MM-DD");
    const presetToStr = moment(presetRange.to).format("YYYY-MM-DD");
    
    return currentFromStr === presetFromStr && currentToStr === presetToStr;
  };

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex flex-wrap gap-2">
        {dateRangePresets.map((preset) => {
          const isActive = isPresetActive(preset);
          return (
            <Button
              key={preset.value}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetClick(preset)}
              className={`h-8 ${isActive ? "bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-yellow-300" : ""}`}
            >
              {preset.label}
            </Button>
          );
        })}
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">From Date</label>
          <DatePicker
            value={value.from}
            onChange={handleFromDateChange}
            placeholder="Select start date"
            className="w-full"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">To Date</label>
          <DatePicker
            value={value.to}
            onChange={handleToDateChange}
            placeholder="Select end date"
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}